import React from "react";
import { getSocialOption } from "../../util/styleUtils";
import SocialButton from "../SocialButton/SocialButton";
import MediaPlayer from "../MediaPlayer/MediaPlayer";
import { isEmbeddableMediaUrl, getPlatformHandler } from "../../util/socialMediaUtils";

const isProfileMediaUrl = (social) => {
  const platformHandler = getPlatformHandler(social?.link);
  if (!platformHandler) return false;

  return (
    (platformHandler.platform === "youtube" &&
      /youtube\.com\/(@|channel\/|c\/|user\/)/.test(social.link)) ||
    (platformHandler.platform === "spotify" &&
      /spotify\.com\/user\//.test(social.link))
  );
};

const splitSocials = (socials) => {
  const processedSocials = [...(socials || [])]?.map((s) => {
    const { icon, rawIcon, id: label } = getSocialOption(s?.link);

    s.meta = {
      icon,
      rawIcon,
      label,
    };

    return s;
  });

  const embeddableMediaSocials =
    processedSocials?.filter((social) => {
      if (!isEmbeddableMediaUrl(social.link)) return false;
      return !isProfileMediaUrl(social);
    }) || [];
  const regularSocials =
    processedSocials?.filter((social) => {
      if (!isEmbeddableMediaUrl(social.link)) return true;
      return isProfileMediaUrl(social);
    }) || [];

  return { processedSocials, embeddableMediaSocials, regularSocials };
};

export const hasMediaEmbeds = (socials) =>
  splitSocials(socials).embeddableMediaSocials.length > 0;

export const hasSocialDisplayName = (social) => {
  if (!social?.platform) return false;
  const { id } = getSocialOption(social?.link);
  return social.platform !== id;
};

export const splitIconAndNamedSocials = (socials) => {
  const { regularSocials } = splitSocials(socials);
  const unnamedSocials = [];
  const namedSocials = [];

  regularSocials.forEach((social) => {
    if (hasSocialDisplayName(social)) {
      namedSocials.push(social);
    } else {
      unnamedSocials.push(social);
    }
  });

  return { unnamedSocials, namedSocials };
};

const SocialButtons = ({ socials, eventType, labelMode }) =>
  socials.map((social, i) => (
    <SocialButton
      key={`regular-${i}`}
      social={social}
      eventType={eventType}
      labelMode={labelMode}
    />
  ));

const SocialsView = ({ socials, eventType, variant = "all" }) => {
  const { processedSocials, embeddableMediaSocials, regularSocials } =
    splitSocials(socials);

  if (!processedSocials?.length) return null;

  if (variant === "icons") {
    if (!regularSocials.length) return null;
    return (
      <SocialButtons
        socials={regularSocials}
        eventType={eventType}
        labelMode="hidden"
      />
    );
  }

  const mediaSection = embeddableMediaSocials.length > 0 && (
    <section className="mt-[var(--inyc-details-stack-gap)]">
      <div className="grid gap-kit-16 grid-cols-1">
        {embeddableMediaSocials.map((social, i) => (
          <div key={`media-player-${i}`} className="min-w-0 w-full max-w-full">
            <MediaPlayer
              social={social}
              className="w-full"
              eventType={eventType}
            />
          </div>
        ))}
      </div>
    </section>
  );

  if (variant === "media") {
    return mediaSection || null;
  }

  return (
    <div className={`relative max-w-full overflow-hidden`}>
      {regularSocials.length > 0 && (
        <section>
          <div className="flex flex-wrap justify-center items-center gap-kit-16">
            <SocialButtons socials={regularSocials} eventType={eventType} />
          </div>
        </section>
      )}
      {mediaSection}
    </div>
  );
};

export default SocialsView;
